<template>
  <Layout>
    <div class="cookie-policy-page">
      <!-- Hero Section -->
      <section class="policy-hero bg-gradient-primary text-white py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-8 mx-auto text-center">
              <h1 class="display-4 font-weight-bold mb-3">Cookie Policy</h1>
              <p class="lead mb-4">Last Updated: August 7, 2025 | Version: 1.0</p>
              <div class="d-flex justify-content-center">
                <b-button variant="light" class="mr-3" @click="scrollToDetails">
                  <b-icon icon="arrow-down" class="mr-2"></b-icon>
                  View Details
                </b-button>
                <b-button variant="outline-light" @click="showCookieSettings">
                  <b-icon icon="gear" class="mr-2"></b-icon>
                  Cookie Settings
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <div class="container my-5">
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <!-- Introduction -->
            <b-card no-body class="border-0 shadow-sm mb-4">
              <b-card-body>
                <div class="d-flex align-items-center mb-4">
                  <div class="icon-circle bg-primary text-white mr-3">
                    <i class="mdi mdi-cookie" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="mb-0">OVVURA – Unlock Radiance!</h3>
                </div>
                <p>OVVURA PORTAL L.L.C maintains the <a href="https://ovvura.ae/">https://ovvura.ae/</a> Website ("Site"). This Cookie Policy explains how we use cookies and similar tracking technologies when you visit or interact with our Site. Please read this policy to understand your choices regarding our use of cookies.</p>
              </b-card-body>
            </b-card>

            <!-- What Are Cookies -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="what-are-cookies">
              <b-card-header class="bg-light">
                <h4 class="mb-0">1. What Are Cookies?</h4>
              </b-card-header>
              <b-card-body>
                <div class="d-flex align-items-start mb-3">
                  <img src="@/assets/images/cookie-illustration.png" alt="Cookie Illustration" class="img-fluid rounded mr-4" style="max-width: 200px;">
                  <div>
                    <p>Cookies are small text files placed on your device when you visit a website. They help the website recognize your device, remember information about your visit, and improve your experience.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- How We Use Cookies -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="how-we-use">
              <b-card-header class="bg-light">
                <h4 class="mb-0">2. How We Use Cookies</h4>
              </b-card-header>
              <b-card-body>
                <p>We use cookies to:</p>
                <ul class="feature-list">
                  <li>
                    <i class="mdi mdi-check-circle text-primary mr-2"></i>
                    Enable core site functionality (essential cookies)
                  </li>
                  <li>
                    <i class="mdi mdi-check-circle text-primary mr-2"></i>
                    Analyze site performance and traffic (performance cookies)
                  </li>
                  <li>
                    <i class="mdi mdi-check-circle text-primary mr-2"></i>
                    Remember your preferences and settings (functional cookies)
                  </li>
                  <li>
                    <i class="mdi mdi-check-circle text-primary mr-2"></i>
                    Deliver personalized content, offers, and advertisements (targeting/marketing cookies)
                  </li>
                </ul>
              </b-card-body>
            </b-card>

            <!-- Types of Cookies -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="types-cookies">
              <b-card-header class="bg-light">
                <h4 class="mb-0">3. Types of Cookies We Use</h4>
              </b-card-header>
              <b-card-body>
                <!-- Essential Cookies -->
                <div class="cookie-type-card mb-4">
                  <div class="d-flex align-items-center mb-2">
                    <div class="icon-circle bg-danger text-white mr-3">
                      <i class="mdi mdi-shield-check" style="font-size: 1.2rem;"></i>
                    </div>
                    <h5 class="mb-0">3.1. Essential Cookies</h5>
                  </div>
                  <p>These cookies are necessary for the Site to function properly. They allow you to navigate pages, access secure areas, and use core features. Without these cookies, some services you have asked for cannot be provided.</p>
                </div>

                <!-- Performance Cookies -->
                <div class="cookie-type-card mb-4">
                  <div class="d-flex align-items-center mb-2">
                    <div class="icon-circle bg-info text-white mr-3">
                      <i class="mdi mdi-chart-line" style="font-size: 1.2rem;"></i>
                    </div>
                    <h5 class="mb-0">3.2. Performance Cookies</h5>
                  </div>
                  <p>Performance cookies collect anonymous information about how visitors use our Site, such as which pages are visited most often and error messages. We use this data to improve how our Site works.</p>
                </div>

                <!-- Functional Cookies -->
                <div class="cookie-type-card mb-4">
                  <div class="d-flex align-items-center mb-2">
                    <div class="icon-circle bg-success text-white mr-3">
                      <i class="mdi mdi-cog" style="font-size: 1.2rem;"></i>
                    </div>
                    <h5 class="mb-0">3.3. Functional Cookies</h5>
                  </div>
                  <p>Functional cookies remember choices you make (e.g., your language, region, login status) to provide enhanced, personalized features.</p>
                </div>

                <!-- Marketing Cookies -->
                <div class="cookie-type-card">
                  <div class="d-flex align-items-center mb-2">
                    <div class="icon-circle bg-warning text-white mr-3">
                      <i class="mdi mdi-bullseye" style="font-size: 1.2rem;"></i>
                    </div>
                    <h5 class="mb-0">3.4. Targeting / Marketing Cookies</h5>
                  </div>
                  <p>These cookies track your browsing habits to deliver relevant advertisements and limit how many times you see the same ad. They may be set by our advertising partners.</p>
                </div>
              </b-card-body>
            </b-card>

            <!-- Third-Party Cookies -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="third-party">
              <b-card-header class="bg-light">
                <h4 class="mb-0">4. Third-Party Cookies</h4>
              </b-card-header>
              <b-card-body>
                <p>We may allow trusted third parties (e.g., Google Analytics, advertising networks) to place cookies through our Site. These cookies are subject to the third party's own policies. OVVURA is not responsible for third-party cookie practices.</p>
              </b-card-body>
            </b-card>

            <!-- Cookie Control -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="cookie-control">
              <b-card-header class="bg-light">
                <h4 class="mb-0">5. Cookie Consent and Control</h4>
              </b-card-header>
              <b-card-body>
                <h5>5.1. Managing Cookies</h5>
                <p>You can control and/or delete cookies as you wish. For details, visit <a href="https://aboutcookies.org" target="_blank">aboutcookies.org</a> or consult your browser's help menu. You can:</p>
                <ul class="feature-list">
                  <li>
                    <i class="mdi mdi-settings text-primary mr-2"></i>
                    Decline non-essential cookies via browser settings
                  </li>
                  <li>
                    <i class="mdi mdi-delete text-primary mr-2"></i>
                    Delete existing cookies from your device at any time
                  </li>
                  <li>
                    <i class="mdi mdi-cookie-check text-primary mr-2"></i>
                    Use our cookie banner to adjust your preferences before browsing
                  </li>
                </ul>

                <h5 class="mt-4">5.2. Impact of Disabling Cookies</h5>
                <p>Disabling or blocking certain cookies may affect your experience on the Site. Some features may not work or may become less efficient.</p>
              </b-card-body>
            </b-card>

            <!-- Updates -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="updates">
              <b-card-header class="bg-light">
                <h4 class="mb-0">6. Updates to This Policy</h4>
              </b-card-header>
              <b-card-body>
                <p>We may update this Cookie Policy from time to time to reflect changes in technology or our practices. We will post any revisions here with a new effective date. It is your responsibility to review this policy periodically.</p>
              </b-card-body>
            </b-card>

            <!-- Contact -->
            <b-card no-body class="border-0 shadow-sm">
              <b-card-header class="bg-light">
                <h4 class="mb-0">7. Contact Us</h4>
              </b-card-header>
              <b-card-body>
                <p>If you have any questions about our use of cookies, please contact us:</p>
                <div class="contact-methods">
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-email text-primary mr-2"></i>
                    <span>Email: <a href="mailto:support@ovvura.ae">support@ovvura.ae</a></span>
                  </div>
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-phone text-primary mr-2"></i>
                    <span>Phone: <a href="tel:+971525495829">+971 52 549 5829</a></span>
                  </div>
                  <div class="contact-method">
                    <i class="mdi mdi-web text-primary mr-2"></i>
                    <span>Website: <a href="https://www.ovvura.ae" target="_blank">https://www.ovvura.ae</a></span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>

      <!-- Cookie Consent Banner -->
      <div class="cookie-consent-banner bg-dark text-white py-3" v-if="showBanner">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-md-8 mb-3 mb-md-0">
              <p class="mb-0">We use cookies to enhance your experience on our website. By continuing to browse, you agree to our use of cookies as described in our <a href="#" class="text-white">Cookie Policy</a>.</p>
            </div>
            <div class="col-md-4 text-md-right">
              <b-button variant="outline-light" size="sm" class="mr-2" @click="showBanner = false">
                Decline
              </b-button>
              <b-button variant="light" size="sm" @click="acceptCookies">
                Accept All
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  data() {
    return {
      showBanner: true
    }
  },
  methods: {
    scrollToDetails() {
      const element = document.getElementById('what-are-cookies');
      element.scrollIntoView({ behavior: 'smooth' });
    },
    showCookieSettings() {
      this.$bvModal.msgBoxOk('Customize your cookie preferences in the modal that would appear here.', {
        title: 'Cookie Settings',
        size: 'lg',
        buttonSize: 'lg',
        okVariant: 'primary',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        centered: true
      });
    },
    acceptCookies() {
      this.showBanner = false;
      // In a real implementation, you would set a cookie here
    }
  }
}
</script>

<style scoped>
.cookie-policy-page {
  overflow-x: hidden;
}

.policy-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 5rem 0;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-list {
  list-style: none;
  padding-left: 0;
}

.feature-list li {
  padding: 8px 0;
  display: flex;
  align-items: center;
}

.cookie-type-card {
  padding: 15px;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.contact-method {
  display: flex;
  align-items: center;
}

.cookie-consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
}

@media (max-width: 768px) {
  .policy-hero {
    padding: 3rem 0;
  }
  
  .policy-hero .btn {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>